import React, { FC, useCallback, useState } from "react";
import { Description, useDialogShadow } from "@evergis/ui";
import { BatchResourcesAclDc } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { getLayerInfo } from "../../../../evergis/core/layer/getLayerInfo";
import { DialogTitle } from "../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../DialogComponents/FullScreenDialog";
import { DialogContent } from "../../DialogComponents/styled";
import { DialogActions } from "../../DialogComponents/DialogActions";
import { LayerAccessControl } from "./LayerAccessControl";
import { updatePermissions } from "../../../../resources/bulkOperations";
import { useDialogInfo, useDialogReset } from "../../../../hooks/useDialogs";
import { useLayerInfo } from "./hooks/useLayerInfo";
import { useAccessNotifications } from "../hooks/useAccessNotifications";
import { useContext } from "../hooks/useContext";
import { ProxyLayerAccess } from "./ProxyLayerAccess";
import { useDefaultScaleRange } from "../../../../hooks/style/useDefaultScaleRange";

export const LayerAccess: FC = () => {
  const {
    resources: [layerName],
  } = useDialogInfo(true);

  const { t } = useTranslation("dialogs");
  const context = useContext();
  const [update, updateState] = updatePermissions.use();
  const closeDialog = useDialogReset();
  const { shadows, setScrollContainer } = useDialogShadow();
  const { layerStructure, loading: structureLoading, layerInfo } = useLayerInfo(layerName);
  const { alias: layerAlias } = layerStructure || {};
  const loading = structureLoading;
  const [updateData, setUpdateData] = useState<BatchResourcesAclDc>();
  const defaultScaleRange = useDefaultScaleRange();
  const layer = getLayerInfo(layerInfo, defaultScaleRange);

  useAccessNotifications(updateState);

  // Коллбэк, вызываемый при клике на кнопку "Сохранить".
  // Производим запрос на обновление acl вложенных сущностей, после чего
  // дергаем эндпоинт добавления сущностей в каталоги пользователей
  const onApply = useCallback(() => {
    if (!updateData) {
      return;
    }

    update({
      input: updateData,
    }).finally(closeDialog);
  }, [closeDialog, update, updateData]);

  return (
    <FullScreenDialog dialogContext={context}>
      <DialogTitle
        displayShadow={shadows.upper}
        titleText={t("access.dialogHeader")}
        style={{ paddingBottom: 0 }}
        extra={<Description style={{ marginTop: 0 }}>{layerAlias}</Description>}
      />
      <DialogContent ref={setScrollContainer} style={{ paddingTop: "1rem" }}>
        {layer.type !== "ProxyService" && (
          <LayerAccessControl layerName={layerName} layerStructure={layerStructure} setUpdateData={setUpdateData} />
        )}
        {layer.type === "ProxyService" && (
          <ProxyLayerAccess layerName={layerName} layer={layer} setUpdateData={setUpdateData} />
        )}
      </DialogContent>
      <DialogActions displayShadow={shadows.bottom} isWaiting={loading || updateState.loading} onApply={onApply} />
    </FullScreenDialog>
  );
};
